import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { object, ref, string } from 'yup';

import { isEmpty } from 'lodash-es';
import useChangePasswordModal from '../../hooks/useChangePasswordModal';
import useUser from '../../hooks/useUser';
import { changePasswordRequest } from '../../redux/ducks/AuthDucks';
import { setChangePasswordModalOpen } from '../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../redux/reducers/RootReducer.types';
import Input from '../inputs/Input';
import Heading from '../layout/Heading';
import Modal from './Modal';

type ChangePasswordInput = {
  newPassword: string;
  confirmNewPassword: string;
};

const ChangePasswordModal = () => {
  const changePasswordModal = useChangePasswordModal();
  const dispatch = useDispatch();
  const { logout } = useUser();
  const { t } = useTranslation();

  const changePasswordResponse = useSelector(
    (state: RootReducerType) => state.auth.changePasswordResponse,
  );
  const onChangePassword = useSelector(
    (state: RootReducerType) => state.auth.onChangePassword,
  );

  const formSchema = object().shape({
    newPassword: string()
      .required(t('static.change_password.validation_error_password_required'))
      .min(6, t('static.change_password.validation_error_password_short'))
      .max(40, t('static.change_password.validation_error_password_long')),
    confirmNewPassword: string()
      .required(
        t('static.change_password.validation_error_confirm_password_required'),
      )
      .min(
        6,
        t('static.change_password.validation_error_confirm_password_short'),
      )
      .max(
        40,
        t('static.change_password.validation_error_confirm_password_long'),
      )
      .oneOf(
        [ref('newPassword')],
        t('static.change_password.validation_error_confirm_password_mismatch'),
      ),
  });

  const {
    register,
    formState: { errors },
    getValues,
    watch,
    trigger,
    reset,
  } = useForm<ChangePasswordInput>({
    resolver: yupResolver(formSchema),
  });

  const onSubmit = () => {
    dispatch(
      changePasswordRequest({
        password: getValues('newPassword'),
      }),
    );
  };

  useEffect(() => {
    watch(() => {
      const newPassword = getValues('newPassword');
      const confirmNewPassword = getValues('confirmNewPassword');

      if (!isEmpty(newPassword) && !isEmpty(confirmNewPassword)) {
        trigger();
      }
    });
  }, [watch]);

  useEffect(() => {
    if (!changePasswordModal.isOpen) {
      reset();
    }
  }, [changePasswordModal.isOpen]);

  useEffect(() => {
    if (changePasswordResponse) {
      if (changePasswordResponse.data) {
        logout();
        changePasswordModal.onClose();
      } else {
        // cannot delete account
        if (
          changePasswordResponse.error &&
          changePasswordResponse.error.indexOf('assword') >= 0
        ) {
          toast.error(
            t('static.change_password.api_error_password_wrong_message'),
          );
          return;
        }

        toast.error(
          t('static.change_password.api_error_cannot_change_message'),
        );
      }
    }
  }, [changePasswordResponse]);

  const changePasswordEnabled = useMemo<boolean>(() => {
    const newPassword = getValues('newPassword');
    const confirmNewPassword = getValues('confirmNewPassword');

    if (!isEmpty(newPassword) && !isEmpty(confirmNewPassword)) {
      return isEmpty(errors);
    }

    return false;
  }, [errors]);

  const bodyContent = (
    <div className="flex flex-col gap-4">
      <Heading
        title={t('static.change_password.modal_header_title')}
        subtitle={t('static.change_password.modal_header_subtitle')}
      />
      <div className="my-6 flex flex-col gap-4">
        <Input
          id="newPassword"
          label={t('static.change_password.modal_input_new_password_label')}
          disabled={false}
          register={register}
          errors={errors}
          options={{
            required: true,
          }}
          type="password"
        />
        <Input
          id="confirmNewPassword"
          label={t(
            'static.change_password.modal_input_confirm_new_password_label',
          )}
          disabled={false}
          register={register}
          errors={errors}
          options={{
            required: true,
          }}
          type="password"
        />
      </div>
    </div>
  );

  return (
    <Modal
      id="ChangePasswordModal"
      setIsOpen={(open) => dispatch(setChangePasswordModalOpen(open))}
      disabled={!changePasswordEnabled}
      loading={onChangePassword}
      isOpen={changePasswordModal.isOpen}
      title={t('static.change_password.modal_title')}
      actionLabel={t('static.change_password.modal_submit_btn_label')}
      secondaryActionLabel={t('static.change_password.modal_cancel_btn_label')}
      secondaryAction={changePasswordModal.onClose}
      onClose={changePasswordModal.onClose}
      onSubmit={onSubmit}
      body={bodyContent}
      noFit
    />
  );
};

export default ChangePasswordModal;
